/* @flow */
/* eslint-env es6,browser */

import * as u from '../utils';

/**
 * @module scheduler
 * @description Implements scheduler functionality
 * @requires module:utils
 */

/**
 * @class _scheduler
 * @description Scheduler base class
 */
class _scheduler<T> {
  _queue: Array<Object>;
  _tid: T;
  delay: number; // used only by timedScheduler
  CB: Object => void;
  prioritizeCB: (Array<Object>) => ?number;
  endCB: () => void;
  constructor() {
    this._queue = [];
    this.delay = 200;
    //FlowFixMe
    this.CB = this.prioritizeCB = this.endCB = u.noop;
  }

  /**
   * set properties
   * @param {number} d - Delay
   * @param {Function} cb - callback function to be invoked for each job
   * @param {Function} pCB - callback for priortizing. Takes input list and returns index
   * @param {Function} eCB - callback function to be invoked after processing all the queued jobs
   * @returns {undefined}
   */
  setProperties(d: number, cb: Function, pCB: Function, eCB: Function) {
    this.delay = d;
    this.CB = cb || (() => u.logError(`scheduler job queued without callback`));
    this.prioritizeCB = pCB || (() => 0);
    this.endCB = eCB || u.noop;
  }

  /**
   * enqueues obj in queue and triggers processing
   * @param {adInfo} obj - Object to be processed on timer callback
   * @returns {undefined}
   */
  enqueue(obj: adInfo) {
    this._queue.push(obj);
    this._processQueue();
  }

  /**
   *
   * Checks the queue for any pending tasks and act accordingly
   * @returns {undefined}
   * @memberof _scheduler
   */
  checkQueue() {
    //there are pending entries to processed
    if (this._isPending()) {
      return;
    }

    //processing completed trigger end of processing CB
    this.endCB();
  }

  /**
   * Clear queue and stop the timer
   * @returns {undefined}
   */
  clear() {
    this._queue.length = 0;
    this._clear();
  }

  /**
   * Process the pending tasks
   * @access private
   * @returns {undefined}
   */
  _processQueue() {
    //schedule only if there is no schedule currently
    if (!this._isPending() && this._queue.length) {
      this._schedule();
    }

    this.checkQueue();
  }

  /**
   * Process specific entry from queue
   * @access private
   * @returns {undefined}
   */
  _processEntry() {
    //reset the timer
    this._clear();
    if (this._queue.length) {
      // prioritize
      let ind = this.prioritizeCB(this._queue) || 0,
        obj = this._queue.splice(ind < 0 ? 0 : ind, 1)[0];
      this.CB(obj);
    }

    // process rest of the queue
    this._processQueue();
  }

  /**
   * Check if there are any pending tasks in queue
   * @access private
   * @returns {boolean} - Queue status
   * @memberof _scheduler
   */
  _isPending() {
    return this._tid !== null;
  }

  _schedule() {
    /**/
  }
  _clear() {
    /**/
  }
}
/**
 * Scheduler using setTimeout
 *
 * @class _timedScheduler
 * @extends {_scheduler}
 */
class _timedScheduler extends _scheduler<?TimeoutID> {
  constructor() {
    super();
    this._tid = null;
  }

  _clear() {
    if (this._tid) {
      clearTimeout(this._tid);
      this._tid = null;
    }
  }

  _schedule() {
    let that = this;
    this._tid = setTimeout(() => that._processEntry(), this.delay);
  }
}

/**
 * Scheduler using requestAnimationFrame(rAF)
 *
 * @class _RAFScheduler
 * @extends {_scheduler}
 */
class _RAFScheduler extends _scheduler<?AnimationFrameID> {
  constructor() {
    super();
    this._tid = null;
  }

  /**
   * Clears pending task
   * @access private
   * @memberof _RAFScheduler
   * @returns {undefined}
   * @override
   */
  _clear() {
    if (this._tid) {
      cancelAnimationFrame(this._tid);
      this._tid = null;
    }
  }
  /**
   *
   * Schedule next RAF
   * @memberof _RAFScheduler
   * @override
   * @access private
   * @returns {undefined}
   */
  _schedule() {
    let that = this;
    this._tid = requestAnimationFrame(() => that._processEntry());
  }
}

export { _RAFScheduler, _timedScheduler };
